import { useState, useEffect, useCallback } from 'react';
import {
  FiUpload, FiX, FiImage, FiTrash2, FiVideo, FiPlus, FiEdit2, FiCheck,
  FiStar, FiMonitor, FiCamera, FiMessageSquare, FiUsers, FiAward, FiBook,
  FiCalendar, FiDollarSign, FiLayout, FiMenu, FiChevronDown, FiPhone,
  FiMapPin, FiShare2, FiClock,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { settingsAPI } from '../../services/api';
import AdminLayout from '../../components/layout/AdminLayout';
import { triggerRefresh } from '../../hooks/useDataRefresh';

// ─── Constants ─────────────────────────────────────────────────────────────────
const emptyTestimonial = { name: '', role: '', rating: 5, text: '' };
const emptyLeader      = { name: '', role: '', description: '', photo: { url: '', publicId: '' } };
const emptyAchievement = { title: '', desc: '' };
const emptyFee         = { level: '', admission: '', monthly: '', annual: '' };
const emptyProgram     = { title: '', grades: '', age: '', colorScheme: 'primary', description: '', subjects: [], highlights: [] };
const emptyCalEvent    = { month: '', event: '' };
const COLOR_OPTIONS    = ['blue', 'primary', 'secondary'];

// ─── Nav groups ────────────────────────────────────────────────────────────────
const NAV_GROUPS = [
  {
    label: 'Organization',
    items: [
      { id: 'org-info', icon: FiBook, label: 'School Information', badge: 'All Pages' },
    ],
  },
  {
    label: 'Media',
    items: [
      { id: 'logo',   icon: FiImage,         label: 'School Logo',     badge: 'Navbar' },
      { id: 'hero',   icon: FiMonitor,       label: 'Hero Background', badge: 'Homepage' },
      { id: 'campus', icon: FiCamera,        label: 'Campus Photo',    badge: 'About · Homepage' },
      { id: 'popup',  icon: FiLayout,        label: 'Popup Banner',    badge: 'All Pages' },
    ],
  },
  {
    label: 'Homepage',
    items: [
      { id: 'testimonials', icon: FiMessageSquare, label: 'Testimonials', badge: 'Homepage' },
    ],
  },
  {
    label: 'About Page',
    items: [
      { id: 'leadership',   icon: FiUsers, label: 'Leadership Team', badge: 'About' },
      { id: 'achievements', icon: FiAward, label: 'Achievements',    badge: 'About' },
    ],
  },
  {
    label: 'Academics',
    items: [
      { id: 'programs', icon: FiBook,     label: 'Programs', badge: 'Home · Academics' },
      { id: 'calendar', icon: FiCalendar, label: 'Calendar', badge: 'Academics' },
    ],
  },
  {
    label: 'Admissions',
    items: [
      { id: 'fees', icon: FiDollarSign, label: 'Fee Structure', badge: 'Admissions' },
    ],
  },
];

const ALL_ITEMS = NAV_GROUPS.flatMap((g) => g.items);

// ─── Spinner ───────────────────────────────────────────────────────────────────
const Spinner = ({ size = 'sm', color = 'white' }) => {
  const sz = size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4';
  const cl = color === 'white' ? 'border-white border-t-transparent' : 'border-red-400 border-t-transparent';
  return <div className={`${sz} border-2 ${cl} rounded-full animate-spin`} />;
};

// ─── Save button ───────────────────────────────────────────────────────────────
const SaveBtn = ({ saving, onClick, disabled, label = 'Save Changes' }) => (
  <button
    onClick={onClick}
    disabled={saving || disabled}
    className="flex items-center gap-1.5 px-4 py-2 bg-[#2563EB] text-white rounded-xl text-sm font-semibold hover:bg-[#1D4ED8] disabled:opacity-60 transition-colors shadow-sm"
  >
    {saving ? <Spinner /> : <FiCheck className="w-3.5 h-3.5" />}
    {saving ? 'Saving…' : label}
  </button>
);

// ─── Card wrapper ──────────────────────────────────────────────────────────────
const Card = ({ children, className = '' }) => (
  <div className={`bg-white rounded-2xl shadow-sm border border-blue-100 p-5 sm:p-6 ${className}`}>
    {children}
  </div>
);

// ─── Section header inside a card ─────────────────────────────────────────────
const CardHeader = ({ title, subtitle, action }) => (
  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
    <div>
      <h3 className="text-base sm:text-lg font-bold text-[#1e3a8a]">{title}</h3>
      {subtitle && <p className="text-slate-500 text-sm mt-0.5">{subtitle}</p>}
    </div>
    {action && <div className="flex-shrink-0">{action}</div>}
  </div>
);

// ─── Upload drop-zone ──────────────────────────────────────────────────────────
const DropZone = ({ onChange, accept, icon: Icon = FiImage, label, hint, disabled }) => (
  <label className={`flex flex-col items-center justify-center w-full h-36 border-2 border-dashed rounded-xl cursor-pointer transition-colors
    ${disabled ? 'opacity-50 cursor-not-allowed border-blue-100' : 'border-blue-200 hover:border-[#2563EB] hover:bg-blue-50/50'}`}>
    <Icon className="w-8 h-8 text-blue-300 mb-2" />
    <span className="text-[#1e3a8a] text-sm font-semibold">{label}</span>
    <span className="text-slate-400 text-xs mt-1">{hint}</span>
    <input type="file" onChange={onChange} className="hidden" accept={accept} disabled={disabled} />
  </label>
);

// ─── Image preview with remove ────────────────────────────────────────────────
const ImagePreviewBox = ({ src, alt, onClear, label, isVideo = false, height = 'h-48' }) => (
  <div className={`relative w-full ${height} rounded-xl overflow-hidden border border-blue-100 bg-slate-50`}>
    {isVideo
      ? <video src={src} className="w-full h-full object-cover" controls muted />
      : <img src={src} alt={alt} className="w-full h-full object-cover" />}
    {onClear && (
      <button
        onClick={onClear}
        className="absolute top-2 right-2 p-1.5 bg-white text-red-500 rounded-full hover:bg-red-50 shadow-sm transition-colors"
      >
        <FiX className="w-4 h-4" />
      </button>
    )}
    {label && (
      <div className="absolute bottom-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded-lg">
        {label}
      </div>
    )}
  </div>
);

// ─── Star picker ───────────────────────────────────────────────────────────────
const StarPicker = ({ value, onChange }) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5].map((s) => (
      <button key={s} type="button" onClick={() => onChange(s)} className="focus:outline-none">
        <FiStar
          className={`w-5 h-5 transition-colors ${s <= value ? 'text-amber-400' : 'text-gray-300'}`}
          style={{ fill: s <= value ? '#f59e0b' : 'none' }}
        />
      </button>
    ))}
  </div>
);

// ─── Form row (inline edit) ────────────────────────────────────────────────────
const inputCls = 'w-full px-3 py-2 border border-blue-100 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]/20 focus:border-[#2563EB] transition-all bg-white';

const FormRow = ({ fields, form, setForm, onCommit, onCancel }) => (
  <div className="bg-blue-50/30 border border-blue-100 rounded-xl p-4 space-y-3">
    {fields.map((f) => (
      <div key={f.key}>
        <label className="block text-xs font-semibold text-slate-600 mb-1">{f.label}{f.required ? ' *' : ''}</label>
        {f.type === 'textarea' ? (
          <textarea rows={3} value={form[f.key] || ''} onChange={(e) => setForm((p) => ({ ...p, [f.key]: e.target.value }))}
            className={`${inputCls} resize-none`} placeholder={f.placeholder || ''} />
        ) : f.type === 'stars' ? (
          <StarPicker value={form[f.key] || 5} onChange={(v) => setForm((p) => ({ ...p, [f.key]: v }))} />
        ) : (
          <input type="text" value={form[f.key] || ''} onChange={(e) => setForm((p) => ({ ...p, [f.key]: e.target.value }))}
            className={inputCls} placeholder={f.placeholder || ''} />
        )}
      </div>
    ))}
    <div className="flex gap-2 pt-1">
      <button onClick={onCommit} className="px-4 py-1.5 bg-[#2563EB] text-white rounded-lg text-sm font-semibold hover:bg-[#1D4ED8] transition-colors shadow-sm">Done</button>
      <button onClick={onCancel} className="px-4 py-1.5 bg-white border border-blue-100 text-slate-600 rounded-lg text-sm font-semibold hover:bg-slate-50 transition-colors shadow-sm">Cancel</button>
    </div>
  </div>
);

// ─── Generic CRUD section ──────────────────────────────────────────────────────
const CrudSection = ({ title, subtitle, items, setItems, emptyItem, fields, saving, onSave }) => {
  const [editing, setEditing] = useState(null);
  const [form, setForm]       = useState(emptyItem);

  const startEdit = (idx) => { setEditing(idx); setForm({ ...items[idx] }); };
  const startNew  = () => { setEditing('new'); setForm({ ...emptyItem }); };
  const cancel    = () => { setEditing(null); setForm(emptyItem); };

  const commit = () => {
    const firstKey = fields[0].key;
    if (!form[firstKey]?.toString().trim()) { toast.error(`${fields[0].label} is required`); return; }
    const updated = editing === 'new' ? [...items, form] : items.map((it, i) => i === editing ? form : it);
    setItems(updated);
    setEditing(null);
    setForm(emptyItem);
  };

  const remove = (idx) => {
    if (!window.confirm('Remove this item?')) return;
    setItems(items.filter((_, i) => i !== idx));
  };

  return (
    <Card>
      <CardHeader title={title} subtitle={subtitle} action={<SaveBtn saving={saving} onClick={onSave} />} />
      <div className="space-y-2">
        {items.length === 0 && editing !== 'new' && (
          <p className="text-gray-400 text-sm text-center py-6">No items yet. Add one below.</p>
        )}
        {items.map((item, idx) =>
          editing === idx ? (
            <FormRow key={idx} fields={fields} form={form} setForm={setForm} onCommit={commit} onCancel={cancel} />
          ) : (
            <div key={idx} className="flex items-center justify-between gap-3 px-4 py-3 bg-white rounded-xl border border-blue-100 hover:border-blue-200 hover:shadow-sm transition-all">
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-[#1e3a8a] text-sm truncate">{item[fields[0].key]}</p>
                {fields[1] && <p className="text-slate-500 text-xs truncate mt-0.5">{item[fields[1].key]}</p>}
              </div>
              <div className="flex gap-1 flex-shrink-0">
                <button onClick={() => startEdit(idx)} className="p-2 text-slate-400 hover:text-[#2563EB] hover:bg-blue-50 rounded-lg transition-colors">
                  <FiEdit2 className="w-4 h-4" />
                </button>
                <button onClick={() => remove(idx)} className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors">
                  <FiTrash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          )
        )}
        {editing === 'new' && <FormRow fields={fields} form={form} setForm={setForm} onCommit={commit} onCancel={cancel} />}
      </div>
      {editing === null && (
        <button onClick={startNew} className="mt-4 flex items-center gap-1.5 text-sm text-[#2563EB] font-semibold hover:text-[#1D4ED8] transition-colors">
          <FiPlus className="w-4 h-4" /> Add {title.replace(/s$/, '')}
        </button>
      )}
    </Card>
  );
};

// ─── Leadership section ────────────────────────────────────────────────────────
const getInitials = (name) =>
  name ? name.split(' ').filter(Boolean).map((n) => n[0]).join('').toUpperCase().slice(0, 2) : '??';

const LeaderForm = ({ form, setForm, photoPreview, onPhotoChange, uploadingPhoto, onCommit, onCancel }) => (
  <div className="bg-blue-50/30 border border-blue-100 rounded-xl p-4 space-y-3">
    <div>
      <label className="block text-xs font-semibold text-slate-600 mb-2">Photo</label>
      <div className="flex items-center gap-4">
        <div className="w-16 h-16 rounded-full overflow-hidden flex-shrink-0 border border-blue-100">
          {photoPreview ? (
            <img src={photoPreview} alt="Preview" className="w-full h-full object-cover" />
          ) : form.photo?.url ? (
            <img src={form.photo.url} alt="Current" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-slate-100 flex items-center justify-center">
              <FiImage className="w-5 h-5 text-slate-400" />
            </div>
          )}
        </div>
        <label className="cursor-pointer flex items-center gap-2 px-3 py-2 border border-dashed border-blue-200 rounded-lg hover:border-[#2563EB] hover:bg-blue-50/50 transition-colors text-sm font-semibold text-slate-600">
          <FiUpload className="w-4 h-4 text-[#2563EB]" />
          {photoPreview || form.photo?.url ? 'Change photo' : 'Upload photo'}
          <input type="file" accept="image/*" onChange={onPhotoChange} className="hidden" />
        </label>
      </div>
    </div>
    {[
      { key: 'name',        label: 'Full Name *',  placeholder: 'e.g. Dr. Ramesh Kumar Sharma' },
      { key: 'role',        label: 'Position *',   placeholder: 'e.g. Principal' },
    ].map((f) => (
      <div key={f.key}>
        <label className="block text-xs font-semibold text-slate-600 mb-1">{f.label}</label>
        <input type="text" value={form[f.key] || ''} onChange={(e) => setForm((p) => ({ ...p, [f.key]: e.target.value }))}
          className={inputCls} placeholder={f.placeholder} />
      </div>
    ))}
    <div>
      <label className="block text-xs font-semibold text-slate-600 mb-1">Description</label>
      <textarea rows={2} value={form.description || ''} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
        className={`${inputCls} resize-none`} placeholder="Brief bio…" />
    </div>
    <div className="flex gap-2 pt-1">
      <button onClick={onCommit} disabled={uploadingPhoto}
        className="flex items-center gap-1.5 px-4 py-1.5 bg-[#2563EB] text-white rounded-lg text-sm font-semibold hover:bg-[#1D4ED8] disabled:opacity-60 transition-colors shadow-sm">
        {uploadingPhoto && <Spinner />}
        {uploadingPhoto ? 'Uploading…' : 'Done'}
      </button>
      <button onClick={onCancel} className="px-4 py-1.5 bg-white border border-blue-100 text-slate-600 rounded-lg text-sm font-semibold hover:bg-slate-50 transition-colors shadow-sm">Cancel</button>
    </div>
  </div>
);

const LeadershipSection = ({ leadership, setLeadership, saving, onSave }) => {
  const [editing,        setEditing]        = useState(null);
  const [form,           setForm]           = useState({ ...emptyLeader });
  const [photoFile,      setPhotoFile]      = useState(null);
  const [photoPreview,   setPhotoPreview]   = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const startEdit = (idx) => { setEditing(idx); setForm({ ...leadership[idx] }); setPhotoFile(null); setPhotoPreview(null); };
  const startNew  = () => { setEditing('new'); setForm({ ...emptyLeader }); setPhotoFile(null); setPhotoPreview(null); };
  const cancel    = () => { setEditing(null); setPhotoFile(null); if (photoPreview) URL.revokeObjectURL(photoPreview); setPhotoPreview(null); };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPhotoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const commit = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (!form.role.trim()) { toast.error('Role is required'); return; }
    let photoData = form.photo || { url: '', publicId: '' };
    if (photoFile) {
      setUploadingPhoto(true);
      try {
        const fd = new FormData();
        fd.append('photo', photoFile);
        if (photoData.publicId) fd.append('oldPublicId', photoData.publicId);
        const res = await settingsAPI.uploadLeadershipPhoto(fd);
        photoData = res.data?.data?.photo || photoData;
      } catch { toast.error('Photo upload failed'); setUploadingPhoto(false); return; }
      finally   { setUploadingPhoto(false); }
    }
    const updated = editing === 'new'
      ? [...leadership, { ...form, photo: photoData }]
      : leadership.map((it, i) => i === editing ? { ...form, photo: photoData } : it);
    setLeadership(updated);
    setEditing(null);
    setPhotoFile(null);
    setPhotoPreview(null);
  };

  const remove = (idx) => {
    if (!window.confirm('Remove this member?')) return;
    setLeadership(leadership.filter((_, i) => i !== idx));
  };

  return (
    <Card>
      <CardHeader title="Leadership Team" subtitle="Shown in 'Meet the Administration' on the About page." action={<SaveBtn saving={saving} onClick={onSave} />} />
      <div className="space-y-2">
        {leadership.length === 0 && editing !== 'new' && <p className="text-gray-400 text-sm text-center py-6">No members yet.</p>}
        {leadership.map((person, idx) =>
          editing === idx ? (
            <LeaderForm key={idx} form={form} setForm={setForm} photoPreview={photoPreview} onPhotoChange={handlePhotoChange} uploadingPhoto={uploadingPhoto} onCommit={commit} onCancel={cancel} />
          ) : (
            <div key={idx} className="flex items-center gap-3 px-4 py-3 bg-white rounded-xl border border-blue-100 hover:border-blue-200 hover:shadow-sm transition-all">
              <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                {person.photo?.url
                  ? <img src={person.photo.url} alt={person.name} className="w-full h-full object-cover" />
                  : <div className="w-full h-full bg-[#2563EB] flex items-center justify-center text-white text-sm font-semibold">{getInitials(person.name)}</div>}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-[#1e3a8a] text-sm truncate">{person.name}</p>
                <p className="text-slate-500 text-xs truncate">{person.role}</p>
              </div>
              <div className="flex gap-1 flex-shrink-0">
                <button onClick={() => startEdit(idx)} className="p-2 text-slate-400 hover:text-[#2563EB] hover:bg-blue-50 rounded-lg transition-colors"><FiEdit2 className="w-4 h-4" /></button>
                <button onClick={() => remove(idx)} className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"><FiTrash2 className="w-4 h-4" /></button>
              </div>
            </div>
          )
        )}
        {editing === 'new' && <LeaderForm form={form} setForm={setForm} photoPreview={photoPreview} onPhotoChange={handlePhotoChange} uploadingPhoto={uploadingPhoto} onCommit={commit} onCancel={cancel} />}
      </div>
      {editing === null && (
        <button onClick={startNew} className="mt-4 flex items-center gap-1.5 text-sm text-[#2563EB] font-semibold hover:text-[#1D4ED8] transition-colors">
          <FiPlus className="w-4 h-4" /> Add Member
        </button>
      )}
    </Card>
  );
};

// ─── Programs section ──────────────────────────────────────────────────────────
const ProgramForm = ({ form, setForm, onCommit, onCancel }) => (
  <div className="bg-blue-50/30 border border-blue-100 rounded-xl p-4 space-y-3">
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-1">Title *</label>
        <input type="text" value={form.title || ''} onChange={(e) => setForm(p => ({ ...p, title: e.target.value }))}
          className={inputCls} placeholder="e.g. Primary School" />
      </div>
      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-1">Color Theme</label>
        <select value={form.colorScheme || 'primary'} onChange={(e) => setForm(p => ({ ...p, colorScheme: e.target.value }))}
          className={`${inputCls} bg-white`}>
          {COLOR_OPTIONS.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-1">Grades</label>
        <input type="text" value={form.grades || ''} onChange={(e) => setForm(p => ({ ...p, grades: e.target.value }))}
          className={inputCls} placeholder="e.g. Grade 1 - 5" />
      </div>
      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-1">Age Range</label>
        <input type="text" value={form.age || ''} onChange={(e) => setForm(p => ({ ...p, age: e.target.value }))}
          className={inputCls} placeholder="e.g. Ages 6-11" />
      </div>
    </div>
    <div>
      <label className="block text-xs font-semibold text-slate-600 mb-1">Description</label>
      <textarea rows={3} value={form.description || ''} onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))}
        className={`${inputCls} resize-none`} placeholder="Program description…" />
    </div>
    <div>
      <label className="block text-xs font-semibold text-slate-600 mb-1">Subjects <span className="text-slate-400 font-normal">(one per line)</span></label>
      <textarea rows={4} value={(form.subjects || []).join('\n')} onChange={(e) => setForm(p => ({ ...p, subjects: e.target.value.split('\n') }))}
        className={`${inputCls} resize-none`} placeholder={"Mathematics\nScience\nEnglish"} />
    </div>
    <div>
      <label className="block text-xs font-semibold text-slate-600 mb-1">Highlights <span className="text-slate-400 font-normal">(one per line)</span></label>
      <textarea rows={3} value={(form.highlights || []).join('\n')} onChange={(e) => setForm(p => ({ ...p, highlights: e.target.value.split('\n') }))}
        className={`${inputCls} resize-none`} placeholder={"Small class sizes\nExperienced teachers"} />
    </div>
    <div className="flex gap-2 pt-1">
      <button onClick={onCommit} className="px-4 py-1.5 bg-[#2563EB] text-white rounded-lg text-sm font-semibold hover:bg-[#1D4ED8] transition-colors shadow-sm">Done</button>
      <button onClick={onCancel} className="px-4 py-1.5 bg-white border border-blue-100 text-slate-600 rounded-lg text-sm font-semibold hover:bg-slate-50 transition-colors shadow-sm">Cancel</button>
    </div>
  </div>
);

const ProgramsSection = ({ programs, setPrograms, saving, onSave }) => {
  const [editing, setEditing] = useState(null);
  const [form,    setForm]    = useState({ ...emptyProgram });

  const startEdit = (idx) => { setEditing(idx); setForm({ ...programs[idx], subjects: programs[idx].subjects || [], highlights: programs[idx].highlights || [] }); };
  const startNew  = () => { setEditing('new'); setForm({ ...emptyProgram }); };
  const cancel    = () => setEditing(null);

  const commit = () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    const cleaned = { ...form, subjects: (form.subjects || []).map(s => s.trim()).filter(Boolean), highlights: (form.highlights || []).map(s => s.trim()).filter(Boolean) };
    const updated = editing === 'new' ? [...programs, cleaned] : programs.map((p, i) => i === editing ? cleaned : p);
    setPrograms(updated);
    setEditing(null);
  };

  const remove = (idx) => {
    if (!window.confirm('Remove this program?')) return;
    setPrograms(programs.filter((_, i) => i !== idx));
  };

  const colorDot = (scheme) => scheme === 'blue' ? 'bg-blue-500' : scheme === 'secondary' ? 'bg-orange-500 text-white' : 'bg-[#2563EB] text-white';

  return (
    <Card>
      <CardHeader title="Academic Programs" subtitle="Shown on the Home and Academics pages." action={<SaveBtn saving={saving} onClick={onSave} />} />
      <div className="space-y-2">
        {programs.length === 0 && editing !== 'new' && <p className="text-slate-400 text-sm text-center py-6">No programs yet.</p>}
        {programs.map((prog, idx) =>
          editing === idx ? (
            <ProgramForm key={idx} form={form} setForm={setForm} onCommit={commit} onCancel={cancel} />
          ) : (
            <div key={idx} className="flex items-center gap-3 px-4 py-3 bg-white rounded-xl border border-blue-100 hover:border-blue-200 hover:shadow-sm transition-all">
              <div className={`w-3 h-3 rounded-full flex-shrink-0 ${colorDot(prog.colorScheme)}`} />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-[#1e3a8a] text-sm truncate">{prog.title}</p>
                <p className="text-slate-500 text-xs">{prog.grades}{prog.age ? ` · ${prog.age}` : ''}</p>
              </div>
              <div className="flex gap-1 flex-shrink-0">
                <button onClick={() => startEdit(idx)} className="p-2 text-slate-400 hover:text-[#2563EB] hover:bg-blue-50 rounded-lg transition-colors"><FiEdit2 className="w-4 h-4" /></button>
                <button onClick={() => remove(idx)} className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"><FiTrash2 className="w-4 h-4" /></button>
              </div>
            </div>
          )
        )}
        {editing === 'new' && <ProgramForm form={form} setForm={setForm} onCommit={commit} onCancel={cancel} />}
      </div>
      {editing === null && (
        <button onClick={startNew} className="mt-4 flex items-center gap-1.5 text-sm text-[#2563EB] font-semibold hover:text-[#1D4ED8] transition-colors">
          <FiPlus className="w-4 h-4" /> Add Program
        </button>
      )}
    </Card>
  );
};

// ─── Mobile nav dropdown ───────────────────────────────────────────────────────
const MobileNav = ({ activeSection, onSelect }) => {
  const [open, setOpen] = useState(false);
  const activeItem = ALL_ITEMS.find((i) => i.id === activeSection);
  const Icon = activeItem?.icon || FiMenu;

  return (
    <div className="relative lg:hidden mb-4">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 px-4 py-3 bg-white border border-blue-100 rounded-xl shadow-sm text-sm font-semibold text-[#1e3a8a] hover:border-blue-200 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-[#2563EB]" />
          <span>{activeItem?.label || 'Select section'}</span>
          <span className="text-xs text-slate-400 font-normal hidden sm:inline">· {activeItem?.badge}</span>
        </div>
        <FiChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute top-full left-0 right-0 mt-1.5 bg-white border border-blue-100 rounded-xl shadow-lg z-20 overflow-hidden">
          {NAV_GROUPS.map((group, gi) => (
            <div key={group.label} className={gi > 0 ? 'border-t border-blue-50' : ''}>
              <p className="px-4 pt-3 pb-1 text-[11px] font-bold text-slate-400 uppercase tracking-wider">{group.label}</p>
              {group.items.map((item) => {
                const ItemIcon = item.icon;
                const active = activeSection === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => { onSelect(item.id); setOpen(false); }}
                    className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-left transition-colors
                      ${active ? 'bg-blue-50 text-[#2563EB] font-semibold' : 'text-slate-600 hover:bg-slate-50'}`}
                  >
                    <ItemIcon className={`w-4 h-4 ${active ? 'text-[#2563EB]' : 'text-slate-400'}`} />
                    {item.label}
                    <span className="ml-auto text-[10px] text-slate-400">{item.badge}</span>
                  </button>
                );
              })}
            </div>
          ))}
          <div className="pb-1" />
        </div>
      )}
    </div>
  );
};

// ─── Breadcrumb ────────────────────────────────────────────────────────────────
const SectionBreadcrumb = ({ activeSection }) => {
  const item = ALL_ITEMS.find((i) => i.id === activeSection);
  if (!item) return null;
  const Icon = item.icon;
  return (
    <div className="flex items-center gap-2 mb-4 text-sm">
      <Icon className="w-4 h-4 text-[#2563EB] flex-shrink-0" />
      <span className="font-semibold text-[#1e3a8a]">{item.label}</span>
      <span className="text-blue-200">·</span>
      <span className="text-xs text-slate-500">Appears on: <span className="font-semibold">{item.badge}</span></span>
    </div>
  );
};

// ─── Organization Info Section ────────────────────────────────────────────────
const OrgInfoSection = ({ orgInfo, setOrgInfo, saving, onSave }) => {
  const inputCls = 'w-full px-3.5 py-2.5 border border-blue-100 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]/20 focus:border-[#2563EB] transition-all bg-white';

  return (
    <Card>
      <CardHeader
        title="School Information"
        subtitle="Organization details displayed across all pages and in footer"
        action={<SaveBtn saving={saving} onClick={onSave} label="Save Info" />}
      />
      
      <div className="space-y-5">
        {/* Basic Info */}
        <div>
          <h4 className="font-bold text-sm text-[#1e3a8a] mb-3 flex items-center gap-2">
            <FiBook className="w-4 h-4 text-[#2563EB]" />
            Basic Information
          </h4>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">School Name</label>
              <input
                type="text"
                value={orgInfo.schoolName || ''}
                onChange={(e) => setOrgInfo(p => ({ ...p, schoolName: e.target.value }))}
                className={inputCls}
                placeholder="e.g. Early Boarding School & Montessori"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Tagline</label>
              <input
                type="text"
                value={orgInfo.schoolTagline || ''}
                onChange={(e) => setOrgInfo(p => ({ ...p, schoolTagline: e.target.value }))}
                className={inputCls}
                placeholder="e.g. Nurturing Future Leaders"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Description</label>
              <textarea
                rows="3"
                value={orgInfo.schoolDescription || ''}
                onChange={(e) => setOrgInfo(p => ({ ...p, schoolDescription: e.target.value }))}
                className={`${inputCls} resize-none`}
                placeholder="Brief description about the school..."
              />
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="border-t border-blue-50 pt-5">
          <h4 className="font-bold text-sm text-[#1e3a8a] mb-3 flex items-center gap-2">
            <FiPhone className="w-4 h-4 text-[#2563EB]" />
            Contact Information
          </h4>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Email Address</label>
              <input
                type="email"
                value={orgInfo.email || ''}
                onChange={(e) => setOrgInfo(p => ({ ...p, email: e.target.value }))}
                className={inputCls}
                placeholder="school@example.com"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Primary Phone</label>
                <input
                  type="tel"
                  value={orgInfo.phone?.primary || ''}
                  onChange={(e) => setOrgInfo(p => ({ ...p, phone: { ...p.phone, primary: e.target.value } }))}
                  className={inputCls}
                  placeholder="+977-1-XXXX-XXXX"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Secondary Phone</label>
                <input
                  type="tel"
                  value={orgInfo.phone?.secondary || ''}
                  onChange={(e) => setOrgInfo(p => ({ ...p, phone: { ...p.phone, secondary: e.target.value } }))}
                  className={inputCls}
                  placeholder="+977-1-XXXX-XXXX (optional)"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Address */}
        <div className="border-t border-blue-50 pt-5">
          <h4 className="font-bold text-sm text-[#1e3a8a] mb-3 flex items-center gap-2">
            <FiMapPin className="w-4 h-4 text-[#2563EB]" />
            Address
          </h4>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Street Address</label>
              <input
                type="text"
                value={orgInfo.address?.street || ''}
                onChange={(e) => setOrgInfo(p => ({ ...p, address: { ...p.address, street: e.target.value } }))}
                className={inputCls}
                placeholder="e.g. Baluwatar, Kathmandu"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">City</label>
                <input
                  type="text"
                  value={orgInfo.address?.city || ''}
                  onChange={(e) => setOrgInfo(p => ({ ...p, address: { ...p.address, city: e.target.value } }))}
                  className={inputCls}
                  placeholder="Kathmandu"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">State/Province</label>
                <input
                  type="text"
                  value={orgInfo.address?.state || ''}
                  onChange={(e) => setOrgInfo(p => ({ ...p, address: { ...p.address, state: e.target.value } }))}
                  className={inputCls}
                  placeholder="Bagmati"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Country</label>
                <input
                  type="text"
                  value={orgInfo.address?.country || ''}
                  onChange={(e) => setOrgInfo(p => ({ ...p, address: { ...p.address, country: e.target.value } }))}
                  className={inputCls}
                  placeholder="Nepal"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Zip Code</label>
              <input
                type="text"
                value={orgInfo.address?.zipCode || ''}
                onChange={(e) => setOrgInfo(p => ({ ...p, address: { ...p.address, zipCode: e.target.value } }))}
                className={inputCls}
                placeholder="Optional"
              />
            </div>
          </div>
        </div>

        {/* Social Media */}
        <div className="border-t border-blue-50 pt-5">
          <h4 className="font-bold text-sm text-[#1e3a8a] mb-3 flex items-center gap-2">
            <FiShare2 className="w-4 h-4 text-[#2563EB]" />
            Social Media
          </h4>
          <div className="space-y-3">
            {[
              { key: 'facebook', label: 'Facebook', icon: 'f', placeholder: 'https://facebook.com/...' },
              { key: 'twitter', label: 'Twitter', icon: '𝕏', placeholder: 'https://twitter.com/...' },
              { key: 'instagram', label: 'Instagram', icon: '📷', placeholder: 'https://instagram.com/...' },
              { key: 'linkedin', label: 'LinkedIn', icon: 'in', placeholder: 'https://linkedin.com/...' },
              { key: 'youtube', label: 'YouTube', icon: '▶', placeholder: 'https://youtube.com/...' },
            ].map((social) => (
              <div key={social.key}>
                <label className="block text-xs font-semibold text-slate-600 mb-1">{social.label}</label>
                <input
                  type="url"
                  value={orgInfo.socialMedia?.[social.key] || ''}
                  onChange={(e) => setOrgInfo(p => ({ ...p, socialMedia: { ...p.socialMedia, [social.key]: e.target.value } }))}
                  className={inputCls}
                  placeholder={social.placeholder}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Office Hours */}
        <div className="border-t border-blue-50 pt-5">
          <h4 className="font-bold text-sm text-[#1e3a8a] mb-3 flex items-center gap-2">
            <FiClock className="w-4 h-4 text-[#2563EB]" />
            Office Hours
          </h4>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Monday - Friday</label>
              <input
                type="text"
                value={orgInfo.officeHours?.monday_friday || ''}
                onChange={(e) => setOrgInfo(p => ({ ...p, officeHours: { ...p.officeHours, monday_friday: e.target.value } }))}
                className={inputCls}
                placeholder="e.g. 8:00 AM - 4:00 PM"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Saturday</label>
              <input
                type="text"
                value={orgInfo.officeHours?.saturday || ''}
                onChange={(e) => setOrgInfo(p => ({ ...p, officeHours: { ...p.officeHours, saturday: e.target.value } }))}
                className={inputCls}
                placeholder="e.g. 10:00 AM - 2:00 PM"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Sunday</label>
              <input
                type="text"
                value={orgInfo.officeHours?.sunday || ''}
                onChange={(e) => setOrgInfo(p => ({ ...p, officeHours: { ...p.officeHours, sunday: e.target.value } }))}
                className={inputCls}
                placeholder="e.g. Closed"
              />
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

// ─── Main component ────────────────────────────────────────────────────────────
const AdminSettings = () => {
  const [loading,        setLoading]        = useState(true);
  const [activeSection,  setActiveSection]  = useState('hero');

  // Logo
  const [logoUrl,     setLogoUrl]     = useState('');
  const [logoFile,    setLogoFile]    = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [savingLogo,  setSavingLogo]  = useState(false);
  const [removingLogo,setRemovingLogo]= useState(false);

  // Campus
  const [campusImageUrl, setCampusImageUrl] = useState('');
  const [campusFile,     setCampusFile]     = useState(null);
  const [campusPreview,  setCampusPreview]  = useState(null);
  const [savingCampus,   setSavingCampus]   = useState(false);

  // Hero
  const [heroMedia,    setHeroMedia]    = useState({ mediaType: 'none', url: '' });
  const [heroMediaType,setHeroMediaType]= useState('image');
  const [heroFile,     setHeroFile]     = useState(null);
  const [heroPreview,  setHeroPreview]  = useState(null);
  const [savingHero,   setSavingHero]   = useState(false);
  const [removingHero, setRemovingHero] = useState(false);

  // Popup
  const [popupBannerUrl, setPopupBannerUrl] = useState('');
  const [popupFile,      setPopupFile]      = useState(null);
  const [popupPreview,   setPopupPreview]   = useState(null);
  const [savingPopup,    setSavingPopup]    = useState(false);
  const [removingPopup,  setRemovingPopup]  = useState(false);

  // Organization Info
  const [orgInfo, setOrgInfo] = useState({
    schoolName: '',
    schoolTagline: '',
    schoolDescription: '',
    email: '',
    phone: { primary: '', secondary: '' },
    address: { street: '', city: '', state: '', country: '', zipCode: '' },
    socialMedia: { facebook: '', twitter: '', instagram: '', linkedin: '', youtube: '' },
    officeHours: { monday_friday: '', saturday: '', sunday: '' },
  });
  const [savingOrgInfo, setSavingOrgInfo] = useState(false);

  // Content
  const [testimonials,    setTestimonials]    = useState([]);
  const [leadership,      setLeadership]      = useState([]);
  const [achievements,    setAchievements]    = useState([]);
  const [feeStructure,    setFeeStructure]    = useState([]);
  const [programs,        setPrograms]        = useState([]);
  const [academicCalendar,setAcademicCalendar]= useState([]);

  const [savingTestimonials, setSavingTestimonials] = useState(false);
  const [savingLeadership,   setSavingLeadership]   = useState(false);
  const [savingAchievements, setSavingAchievements] = useState(false);
  const [savingFees,         setSavingFees]         = useState(false);
  const [savingPrograms,     setSavingPrograms]      = useState(false);
  const [savingCalendar,     setSavingCalendar]      = useState(false);

  const loadSettings = (showLoader = false) => {
    if (showLoader) setLoading(true);
    return settingsAPI.get().then((res) => {
      const s = res.data?.data?.settings;
      if (!s) return;
      if (s?.logo?.url) setLogoUrl(s.logo.url);
      if (s?.campusImage?.url) setCampusImageUrl(s.campusImage.url);
      if (s?.heroMedia?.mediaType && s.heroMedia.mediaType !== 'none') {
        setHeroMedia(s.heroMedia);
        setHeroMediaType(s.heroMedia.mediaType);
      }
      if (s?.popupBanner?.url) setPopupBannerUrl(s.popupBanner.url);
      setOrgInfo({
        schoolName: s?.schoolName || '',
        schoolTagline: s?.schoolTagline || '',
        schoolDescription: s?.schoolDescription || '',
        email: s?.email || '',
        phone: s?.phone || { primary: '', secondary: '' },
        address: s?.address || { street: '', city: '', state: '', country: '', zipCode: '' },
        socialMedia: s?.socialMedia || { facebook: '', twitter: '', instagram: '', linkedin: '', youtube: '' },
        officeHours: s?.officeHours || { monday_friday: '', saturday: '', sunday: '' },
      });
      setTestimonials(s.testimonials || []);
      setLeadership(s.leadership || []);
      setAchievements(s.achievements || []);
      setFeeStructure(s.feeStructure || []);
      setPrograms(s.programs || []);
      setAcademicCalendar(s.academicCalendar || []);
    }).catch(() => {
      toast.error('Failed to load settings. Please refresh the page.');
    }).finally(() => setLoading(false));
  };

  useEffect(() => { loadSettings(true); }, []);

  // ── Logo ──
  const handleLogoFileChange = (e) => {
    const file = e.target.files[0]; if (!file) return;
    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setLogoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };
  const handleLogoUpload = async () => {
    if (!logoFile) { toast.error('Please select an image'); return; }
    setSavingLogo(true);
    try {
      const fd = new FormData(); fd.append('image', logoFile);
      const res = await settingsAPI.updateLogo(fd);
      const url = res.data?.data?.settings?.logo?.url;
      if (url) setLogoUrl(url);
      setLogoFile(null); setLogoPreview(null);
      toast.success('School logo updated');
      triggerRefresh('siteSettings');
    } catch (err) { toast.error(err.response?.data?.message || 'Upload failed'); }
    finally { setSavingLogo(false); }
  };
  const handleLogoRemove = async () => {
    if (!window.confirm('Remove the school logo?')) return;
    setRemovingLogo(true);
    try {
      await settingsAPI.removeLogo();
      setLogoUrl(''); setLogoFile(null); setLogoPreview(null);
      toast.success('Logo removed');
      triggerRefresh('siteSettings');
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to remove'); }
    finally { setRemovingLogo(false); }
  };

  // ── Campus ──
  const handleCampusFileChange = (e) => {
    const file = e.target.files[0]; if (!file) return;
    setCampusFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setCampusPreview(ev.target.result);
    reader.readAsDataURL(file);
  };
  const handleCampusUpload = async () => {
    if (!campusFile) { toast.error('Please select an image'); return; }
    setSavingCampus(true);
    try {
      const fd = new FormData(); fd.append('image', campusFile);
      const res = await settingsAPI.updateCampusImage(fd);
      const url = res.data?.data?.settings?.campusImage?.url;
      if (url) setCampusImageUrl(url);
      setCampusFile(null); setCampusPreview(null);
      toast.success('Campus photo updated');
      triggerRefresh('siteSettings');
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to upload'); }
    finally { setSavingCampus(false); }
  };

  // ── Hero ──
  const handleHeroFileChange = (e) => {
    const file = e.target.files[0]; if (!file) return;
    setHeroFile(file);
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (ev) => setHeroPreview({ type: 'image', src: ev.target.result });
      reader.readAsDataURL(file);
    } else {
      setHeroPreview({ type: 'video', src: URL.createObjectURL(file) });
    }
  };
  const handleHeroUpload = async () => {
    if (!heroFile) { toast.error('Please select a file'); return; }
    setSavingHero(true);
    try {
      const fd = new FormData(); fd.append('media', heroFile);
      const res = await settingsAPI.updateHeroMedia(fd);
      const media = res.data?.data?.settings?.heroMedia;
      if (media) setHeroMedia(media);
      setHeroFile(null); setHeroPreview(null);
      toast.success('Hero background updated');
      triggerRefresh('siteSettings');
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to upload. Videos must be MP4/WebM under 100MB.'); }
    finally { setSavingHero(false); }
  };
  const handleHeroRemove = async () => {
    setRemovingHero(true);
    try {
      await settingsAPI.removeHeroMedia();
      setHeroMedia({ mediaType: 'none', url: '' }); setHeroFile(null); setHeroPreview(null);
      toast.success('Hero background removed');
      triggerRefresh('siteSettings');
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to remove'); }
    finally { setRemovingHero(false); }
  };
  const clearHeroSelection = useCallback(() => {
    setHeroFile(null);
    if (heroPreview?.type === 'video') URL.revokeObjectURL(heroPreview.src);
    setHeroPreview(null);
  }, [heroPreview]);

  // ── Popup ──
  const handlePopupFileChange = (e) => {
    const file = e.target.files[0]; if (!file) return;
    setPopupFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPopupPreview(ev.target.result);
    reader.readAsDataURL(file);
  };
  const handlePopupUpload = async () => {
    if (!popupFile) { toast.error('Please select an image'); return; }
    setSavingPopup(true);
    try {
      const fd = new FormData(); fd.append('image', popupFile);
      const res = await settingsAPI.updatePopupBanner(fd);
      const url = res.data?.data?.settings?.popupBanner?.url;
      if (url) setPopupBannerUrl(url);
      setPopupFile(null); setPopupPreview(null);
      toast.success('Popup banner updated');
      triggerRefresh('siteSettings');
    } catch (err) { toast.error(err.response?.data?.message || 'Upload failed'); }
    finally { setSavingPopup(false); }
  };
  const handlePopupRemove = async () => {
    if (!window.confirm('Remove the popup banner?')) return;
    setRemovingPopup(true);
    try {
      await settingsAPI.removePopupBanner();
      setPopupBannerUrl(''); setPopupFile(null); setPopupPreview(null);
      toast.success('Popup banner removed');
      triggerRefresh('siteSettings');
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to remove'); }
    finally { setRemovingPopup(false); }
  };

  // ── Organization Info Save ──
  const saveOrgInfo = async () => {
    setSavingOrgInfo(true);
    try {
      await settingsAPI.updateOrgInfo(orgInfo);
      toast.success('Organization information updated');
      await loadSettings();
      triggerRefresh('siteSettings');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save organization info');
    } finally {
      setSavingOrgInfo(false);
    }
  };

  // ── Content saves ──
  const saveTestimonials = async () => {
    setSavingTestimonials(true);
    try {
      await settingsAPI.updateTestimonials({ testimonials });
      toast.success('Testimonials saved');
      await loadSettings();
      triggerRefresh('siteSettings');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally { setSavingTestimonials(false); }
  };
  const saveLeadership = async () => {
    setSavingLeadership(true);
    try {
      await settingsAPI.updateLeadership({ leadership });
      toast.success('Leadership team saved');
      await loadSettings();
      triggerRefresh('siteSettings');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally { setSavingLeadership(false); }
  };
  const saveAchievements = async () => {
    setSavingAchievements(true);
    try {
      await settingsAPI.updateAchievements({ achievements });
      toast.success('Achievements saved');
      await loadSettings();
      triggerRefresh('siteSettings');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally { setSavingAchievements(false); }
  };
  const saveFees = async () => {
    setSavingFees(true);
    try {
      await settingsAPI.updateFeeStructure({ feeStructure });
      toast.success('Fee structure saved');
      await loadSettings();
      triggerRefresh('siteSettings');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally { setSavingFees(false); }
  };
  const savePrograms = async () => {
    setSavingPrograms(true);
    try {
      await settingsAPI.updatePrograms({ programs });
      toast.success('Programs saved');
      await loadSettings();
      triggerRefresh('siteSettings');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally { setSavingPrograms(false); }
  };
  const saveCalendar = async () => {
    setSavingCalendar(true);
    try {
      await settingsAPI.updateAcademicCalendar({ academicCalendar });
      toast.success('Academic calendar saved');
      await loadSettings();
      triggerRefresh('siteSettings');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally { setSavingCalendar(false); }
  };

  // ── Checkerboard style for transparent PNGs ──
  const checkerboard = { background: 'repeating-conic-gradient(#e5e7eb 0% 25%, #ffffff 0% 50%) 0 0 / 20px 20px' };

  if (loading) return (
    <AdminLayout>
      <div className="flex items-center justify-center py-24">
        <div className="w-8 h-8 border-4 border-[#2563EB]/20 border-t-[#2563EB] rounded-full animate-spin" />
      </div>
    </AdminLayout>
  );

  return (
    <AdminLayout>
      {/* Page header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-[#1e3a8a]">Site Settings</h2>
        <p className="text-slate-500 text-sm mt-1">Manage website content and appearance. Changes are saved per section.</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6 items-start">

        {/* ── Desktop sidebar ── */}
        <aside className="hidden lg:block w-56 flex-shrink-0 sticky top-6">
          <nav className="bg-white rounded-2xl shadow-sm border border-blue-100 overflow-hidden">
            {NAV_GROUPS.map((group, gi) => (
              <div key={group.label} className={gi > 0 ? 'border-t border-blue-50' : ''}>
                <p className="px-4 pt-4 pb-2 text-[11px] font-bold text-slate-400 uppercase tracking-wider">{group.label}</p>
                {group.items.map((item) => {
                  const Icon = item.icon;
                  const active = activeSection === item.id;
                  return (
                    <button
                      key={item.id}
                      onClick={() => setActiveSection(item.id)}
                      className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-left transition-colors
                        ${active ? 'bg-blue-50 text-[#2563EB] font-semibold border-l-4 border-[#2563EB]' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900 border-l-4 border-transparent'}`}
                    >
                      <Icon className={`w-4 h-4 flex-shrink-0 ${active ? 'text-[#2563EB]' : 'text-slate-400'}`} />
                      <span className="flex-1">{item.label}</span>
                    </button>
                  );
                })}
                {gi === NAV_GROUPS.length - 1 && <div className="pb-1" />}
              </div>
            ))}
          </nav>
          <p className="mt-4 text-xs text-slate-400 leading-relaxed px-1">
            Click <span className="font-semibold text-slate-500">Save Changes</span> after editing each section.
          </p>
        </aside>

        {/* ── Content area ── */}
        <div className="flex-1 min-w-0 w-full">

          {/* Mobile dropdown nav */}
          <MobileNav activeSection={activeSection} onSelect={setActiveSection} />

          {/* Desktop breadcrumb */}
          <div className="hidden lg:block">
            <SectionBreadcrumb activeSection={activeSection} />
          </div>

          {/* ══ Organization Info ══ */}
          {activeSection === 'org-info' && (
            <OrgInfoSection orgInfo={orgInfo} setOrgInfo={setOrgInfo} saving={savingOrgInfo} onSave={saveOrgInfo} />
          )}

          {/* ══ School Logo ══ */}
          {activeSection === 'logo' && (
            <Card>
              <CardHeader
                title="School Logo"
                subtitle="Displayed in the navbar on all pages. PNG with transparent background recommended."
              />

              {/* Navbar mockup preview */}
              <div className="mb-6 rounded-xl overflow-hidden border border-blue-100 bg-white">
                <div className="bg-[#1e3a8a] px-4 py-3 flex items-center gap-3">
                  {(logoPreview || logoUrl) ? (
                    <div className="flex items-center justify-center bg-white/95 rounded-lg p-1.5 shadow-sm shrink-0">
                      <img src={logoPreview || logoUrl} alt="Logo preview" className="h-10 sm:h-14 w-auto max-w-[140px] object-contain block" />
                    </div>
                  ) : (
                    <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-lg bg-white/10 border border-white/20 flex items-center justify-center shrink-0">
                      <FiImage className="w-5 h-5 sm:w-6 sm:h-6 text-white/60" />
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="font-bold text-white text-xs sm:text-sm leading-tight truncate">Early Boarding School & Montessori</p>
                    <p className="text-blue-200 text-[10px] font-medium uppercase tracking-wider">Inspiring Excellence</p>
                  </div>
                  <div className="ml-auto hidden sm:flex items-center gap-2">
                    <div className="h-7 w-14 rounded-md bg-white/10" />
                    <div className="h-7 w-14 rounded-md bg-white/10" />
                    <div className="h-8 w-18 rounded-lg bg-[#2563EB]" />
                  </div>
                </div>
                <p className="text-center text-xs text-slate-400 py-2 bg-slate-50 border-t border-blue-50">Navbar preview</p>
              </div>

              {/* Current logo */}
              {logoUrl && !logoPreview && (
                <div className="mb-5">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Current Logo</p>
                    <button onClick={handleLogoRemove} disabled={removingLogo}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-red-600 bg-red-50 rounded-lg hover:bg-red-100 disabled:opacity-60 transition-colors">
                      {removingLogo ? <Spinner color="red" /> : <FiTrash2 className="w-3 h-3" />}
                      {removingLogo ? 'Removing…' : 'Remove Logo'}
                    </button>
                  </div>
                  <div className="flex items-center justify-center h-48 sm:h-64 rounded-xl border border-blue-100 overflow-hidden" style={checkerboard}>
                    <img src={logoUrl} alt="Current Logo" className="max-h-40 sm:max-h-52 max-w-full w-auto object-contain drop-shadow-sm" />
                  </div>
                  <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                    <FiCheck className="w-3.5 h-3.5" /> Logo is active in the navbar.
                  </p>
                </div>
              )}

              {/* Preview new */}
              {logoPreview ? (
                <div className="relative mb-5">
                  <div className="flex items-center justify-center h-48 sm:h-64 rounded-xl border border-blue-100 overflow-hidden" style={checkerboard}>
                    <img src={logoPreview} alt="Preview" className="max-h-40 sm:max-h-52 max-w-full w-auto object-contain drop-shadow-sm" />
                  </div>
                  <button onClick={() => { setLogoPreview(null); setLogoFile(null); }}
                    className="absolute top-3 right-3 p-1.5 bg-white text-red-500 rounded-full hover:bg-red-50 shadow-sm transition-colors">
                    <FiX className="w-4 h-4" />
                  </button>
                  <p className="text-xs text-slate-400 text-center mt-2">New logo — not saved yet</p>
                </div>
              ) : (
                <DropZone
                  onChange={handleLogoFileChange}
                  accept="image/*"
                  icon={FiImage}
                  label={logoUrl ? 'Click to replace logo' : 'Click to upload school logo'}
                  hint="PNG (transparent bg recommended) · JPG · WebP · up to 5MB"
                  className="mb-5"
                />
              )}

              {logoFile && (
                <button onClick={handleLogoUpload} disabled={savingLogo}
                  className="flex items-center gap-2 px-5 py-2.5 bg-[#2563EB] text-white rounded-xl font-semibold hover:bg-[#1D4ED8] disabled:opacity-70 transition-colors text-sm shadow-sm mt-4">
                  {savingLogo ? <Spinner size="md" /> : <FiUpload className="w-4 h-4" />}
                  {savingLogo ? 'Uploading…' : 'Save Logo'}
                </button>
              )}
            </Card>
          )}

          {/* ══ Hero Background ══ */}
          {activeSection === 'hero' && (
            <Card>
              <CardHeader title="Hero Background" subtitle="Upload a photo or intro video shown behind the main headline." />

              {/* Current media */}
              {heroMedia.mediaType !== 'none' && !heroPreview && (
                <div className="mb-5">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs text-slate-500 font-semibold uppercase tracking-wider">
                      Current {heroMedia.mediaType === 'video' ? 'Video' : 'Image'}
                    </p>
                    <button onClick={handleHeroRemove} disabled={removingHero}
                      className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold text-red-600 bg-red-50 rounded-lg hover:bg-red-100 disabled:opacity-60 transition-colors">
                      {removingHero ? <Spinner size="sm" color="red" /> : <FiTrash2 className="w-3 h-3" />}
                      {removingHero ? 'Removing…' : 'Remove'}
                    </button>
                  </div>
                  <div className="w-full h-44 sm:h-56 rounded-xl overflow-hidden border border-blue-100 bg-slate-900">
                    {heroMedia.mediaType === 'video'
                      ? <video src={heroMedia.url} className="w-full h-full object-cover" controls muted />
                      : <img src={heroMedia.url} alt="Hero" className="w-full h-full object-cover" />}
                  </div>
                </div>
              )}

              {/* Type toggle */}
              <div className="flex gap-2 mb-4">
                {['image', 'video'].map((t) => (
                  <button key={t} onClick={() => { setHeroMediaType(t); clearHeroSelection(); }}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border transition-colors
                      ${heroMediaType === t ? 'bg-[#2563EB] text-white border-[#2563EB] shadow-sm' : 'bg-white text-slate-600 border-blue-100 hover:border-blue-200 hover:bg-blue-50'}`}>
                    {t === 'video' ? <FiVideo className="w-4 h-4" /> : <FiImage className="w-4 h-4" />}
                    {t.charAt(0).toUpperCase() + t.slice(1)}
                  </button>
                ))}
              </div>

              {/* Preview new */}
              {heroPreview ? (
                <div className="mb-4">
                  <ImagePreviewBox
                    src={heroPreview.src}
                    alt="Preview"
                    isVideo={heroPreview.type === 'video'}
                    onClear={clearHeroSelection}
                    label={`New ${heroPreview.type} — not saved yet`}
                    height="h-44 sm:h-56"
                  />
                </div>
              ) : (
                <DropZone
                  key={heroMediaType}
                  onChange={handleHeroFileChange}
                  accept={heroMediaType === 'video' ? 'video/mp4,video/webm,video/ogg' : 'image/*'}
                  icon={heroMediaType === 'video' ? FiVideo : FiImage}
                  label={`Click to select ${heroMediaType}`}
                  hint={heroMediaType === 'video' ? 'MP4, WebM up to 100MB' : 'JPG, PNG, WebP up to 5MB'}
                />
              )}

              {heroFile && (
                <button onClick={handleHeroUpload} disabled={savingHero}
                  className="mt-4 flex items-center gap-2 px-5 py-2.5 bg-[#2563EB] text-white rounded-xl font-semibold hover:bg-[#1D4ED8] disabled:opacity-70 transition-colors text-sm shadow-sm">
                  {savingHero ? <Spinner size="md" /> : <FiUpload className="w-4 h-4" />}
                  {savingHero ? 'Uploading…' : `Save ${heroMediaType === 'video' ? 'Video' : 'Image'}`}
                </button>
              )}
            </Card>
          )}

          {/* ══ Campus Photo ══ */}
          {activeSection === 'campus' && (
            <Card>
              <CardHeader title="Campus Photo" subtitle="Displayed in the About section on the homepage alongside school info." />

              {campusImageUrl && !campusPreview && (
                <div className="mb-4">
                  <p className="text-xs text-slate-500 font-semibold uppercase tracking-wider mb-2">Current Photo</p>
                  <div className="w-full h-44 sm:h-56 rounded-xl overflow-hidden border border-blue-100">
                    <img src={campusImageUrl} alt="Campus" className="w-full h-full object-cover" />
                  </div>
                </div>
              )}

              {campusPreview ? (
                <div className="mb-4">
                  <ImagePreviewBox src={campusPreview} alt="Preview" onClear={() => { setCampusPreview(null); setCampusFile(null); }} height="h-44 sm:h-56" />
                </div>
              ) : (
                <DropZone onChange={handleCampusFileChange} accept="image/*" label="Click to upload campus photo" hint="JPG, PNG, WebP up to 5MB" />
              )}

              {campusFile && (
                <button onClick={handleCampusUpload} disabled={savingCampus}
                  className="mt-4 flex items-center gap-2 px-5 py-2.5 bg-[#2563EB] text-white rounded-xl font-semibold hover:bg-[#1D4ED8] disabled:opacity-70 transition-colors text-sm shadow-sm">
                  {savingCampus ? <Spinner size="md" /> : <FiUpload className="w-4 h-4" />}
                  {savingCampus ? 'Uploading…' : 'Save Campus Photo'}
                </button>
              )}
            </Card>
          )}

          {/* ══ Popup Banner ══ */}
          {activeSection === 'popup' && (
            <Card>
              <CardHeader title="Popup Banner" subtitle="Appears when visitors open or refresh any public page. Remove to disable." />

              {popupBannerUrl && !popupPreview && (
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Current Banner</p>
                    <button onClick={handlePopupRemove} disabled={removingPopup}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-red-600 bg-red-50 rounded-lg hover:bg-red-100 disabled:opacity-60 transition-colors">
                      {removingPopup ? <Spinner color="red" /> : <FiTrash2 className="w-3 h-3" />}
                      {removingPopup ? 'Removing…' : 'Remove Banner'}
                    </button>
                  </div>
                  <div className="w-full rounded-xl overflow-hidden border border-blue-100 bg-slate-50">
                    <img src={popupBannerUrl} alt="Popup Banner" className="w-full h-auto object-contain max-h-64" />
                  </div>
                  <p className="text-xs text-emerald-600 mt-2 flex items-center gap-1">
                    <FiCheck className="w-3.5 h-3.5" /> Banner is active — visitors will see this popup on page load.
                  </p>
                </div>
              )}

              {popupPreview ? (
                <div className="mb-4 relative w-full rounded-xl overflow-hidden border border-blue-100 bg-slate-50">
                  <img src={popupPreview} alt="Preview" className="w-full h-auto object-contain max-h-64" />
                  <button onClick={() => { setPopupPreview(null); setPopupFile(null); }}
                    className="absolute top-2 right-2 p-1.5 bg-white text-red-500 rounded-full hover:bg-red-50 shadow-sm transition-colors">
                    <FiX className="w-4 h-4" />
                  </button>
                  <p className="text-xs text-slate-400 text-center py-2 border-t border-blue-50">New image — not saved yet</p>
                </div>
              ) : (
                <DropZone
                  onChange={handlePopupFileChange}
                  accept="image/*"
                  label={popupBannerUrl ? 'Click to replace banner image' : 'Click to upload banner image'}
                  hint="JPG, PNG, WebP up to 5MB"
                />
              )}

              {popupFile && (
                <button onClick={handlePopupUpload} disabled={savingPopup}
                  className="mt-4 flex items-center gap-2 px-5 py-2.5 bg-[#2563EB] text-white rounded-xl font-semibold hover:bg-[#1D4ED8] disabled:opacity-70 transition-colors text-sm shadow-sm">
                  {savingPopup ? <Spinner size="md" /> : <FiUpload className="w-4 h-4" />}
                  {savingPopup ? 'Uploading…' : 'Save Popup Banner'}
                </button>
              )}
            </Card>
          )}

          {/* ══ Testimonials ══ */}
          {activeSection === 'testimonials' && (
            <CrudSection
              title="Testimonials"
              subtitle="Shown in the 'What People Say' carousel on the homepage."
              items={testimonials}
              setItems={setTestimonials}
              emptyItem={emptyTestimonial}
              saving={savingTestimonials}
              onSave={saveTestimonials}
              fields={[
                { key: 'name',   label: 'Name',   required: true, placeholder: 'e.g. Anjali Sharma' },
                { key: 'role',   label: 'Role',   required: true, placeholder: 'e.g. Parent of Grade 8 Student' },
                { key: 'rating', label: 'Rating', type: 'stars' },
                { key: 'text',   label: 'Quote',  required: true, type: 'textarea', placeholder: 'Their testimonial…' },
              ]}
            />
          )}

          {/* ══ Leadership Team ══ */}
          {activeSection === 'leadership' && (
            <LeadershipSection
              leadership={leadership}
              setLeadership={setLeadership}
              saving={savingLeadership}
              onSave={saveLeadership}
            />
          )}

          {/* ══ Achievements ══ */}
          {activeSection === 'achievements' && (
            <CrudSection
              title="Achievements"
              subtitle="Shown in the 'Achievements & Recognition' section on the About page."
              items={achievements}
              setItems={setAchievements}
              emptyItem={emptyAchievement}
              saving={savingAchievements}
              onSave={saveAchievements}
              fields={[
                { key: 'title', label: 'Title',       required: true, placeholder: 'e.g. Best School Award 2024' },
                { key: 'desc',  label: 'Description', type: 'textarea', placeholder: 'Brief description…' },
              ]}
            />
          )}

          {/* ══ Academic Programs ══ */}
          {activeSection === 'programs' && (
            <ProgramsSection programs={programs} setPrograms={setPrograms} saving={savingPrograms} onSave={savePrograms} />
          )}

          {/* ══ Academic Calendar ══ */}
          {activeSection === 'calendar' && (
            <CrudSection
              title="Academic Calendar"
              subtitle="Shown on the Academics page under 'Planning Ahead'."
              items={academicCalendar}
              setItems={setAcademicCalendar}
              emptyItem={emptyCalEvent}
              saving={savingCalendar}
              onSave={saveCalendar}
              fields={[
                { key: 'month', label: 'Month', required: true, placeholder: 'e.g. April' },
                { key: 'event', label: 'Event', required: true, placeholder: 'e.g. New Academic Session Begins' },
              ]}
            />
          )}

          {/* ══ Fee Structure ══ */}
          {activeSection === 'fees' && (
            <CrudSection
              title="Fee Structure"
              subtitle="Shown in the 'Tuition & Fee Structure' table on the Admissions page."
              items={feeStructure}
              setItems={setFeeStructure}
              emptyItem={emptyFee}
              saving={savingFees}
              onSave={saveFees}
              fields={[
                { key: 'level',     label: 'Level',         required: true, placeholder: 'e.g. Primary (Grade 1-5)' },
                { key: 'admission', label: 'Admission Fee',  placeholder: 'e.g. NPR 15,000' },
                { key: 'monthly',   label: 'Monthly Fee',    placeholder: 'e.g. NPR 3,500' },
                { key: 'annual',    label: 'Annual Fee',     placeholder: 'e.g. NPR 8,000' },
              ]}
            />
          )}

        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminSettings;
